import { Widget } from '@/components/widget';
import { FormEvent, useRef, useState, SyntheticEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { LinkIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { SelectMenu } from '@/components/select-menu';
import { useDataClass } from '@/hooks/use-data-class';
import { useDataClassRefine } from '@/hooks/use-data-class-refine';
import { useDataClassCreate } from '@/hooks/use-data-class-create';
import { Modal } from '@/components/modal';
import { modalText } from '@/config/modal';
import { Button } from '@/components/button';
import { ObjectData, useObjectSchemaUpsert } from '@/hooks/use-object-schema-upsert';
import { useSchemaDelete } from '@/hooks/use-schema-delete';
import { DragDrop } from '@/components/drag-drop';
import { useListener } from '@/hooks/use-listener';
import { useEmitter } from '@/hooks/use-emitter';
import { useTabId } from '@/hooks/use-tab-id';
import { useScreenId } from '@/hooks/use-screen-id';
import { WidgetID } from '@/widget-store';
import { createPortal } from 'react-dom';
import clsx from 'clsx';

interface ContainerDropData {
  type: string;
  value: Array<{ uuid: string; type: string; label: string }>;
}

const schemaTypeOptions = [
  { label: 'Data', value: 'data' },
  { label: 'Vault Item', value: 'vaultItem' },
];

export function SchemaDetailsWidget({ id }: { id: WidgetID }) {
  const [searchParams] = useSearchParams();
  const formRef = useRef<HTMLFormElement>(null);

  const schemaFromUrl = searchParams.get('schema');
  const titleFromUrl = searchParams.get('title');
  const schemaTypeFromUrl = searchParams.get('schemaType') ?? 'data';

  const [schema, setSchema] = useState<object>(() => {
    if (schemaFromUrl) { try { return JSON.parse(schemaFromUrl); } catch { return {}; } }
    return {};
  });
  const [uiSchema, setUiSchema] = useState<object>({});
  const [schemaType, setSchemaType] = useState<{ label: string; value: string }>(
    schemaTypeOptions.find((o) => o.value === schemaTypeFromUrl) ?? schemaTypeOptions[0]
  );
  const [loadedDataClass, setLoadedDataClass] = useState<any>(
    titleFromUrl ? { title: titleFromUrl } : {}
  );
  const [titleValue, setTitleValue] = useState(titleFromUrl ?? '');
  const [container, setContainer] = useState('');
  const [error, setError] = useState<string>();
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [createNewDataClassLoading, setCreateNewDataClassLoading] = useState(false);
  const [refineDataClassLoading, setRefineDataClassLoading] = useState(false);

  const tabId = useTabId();
  const screenId = useScreenId();
  const emit = useEmitter({ id, listenersPrefix: `${tabId}/${screenId}` });

  const dataClassLoad = useDataClass();
  const dataClassRefine = useDataClassRefine();
  const dataClassCreate = useDataClassCreate();
  const objectSchemaUpsertMutation = useObjectSchemaUpsert();
  const objectSchemaDeleteMutation = useSchemaDelete();

  useListener({
    id,
    type: 'DataClass',
    listenerFn: (data) => {
      const uuid = data?.[0]?.uuid;
      if (uuid) handleLoadByUuid(uuid);
      else handleNew();
    },
  });

  useListener({
    id,
    type: 'SchemaChanged',
    listenerFn: (data) => {
      const item = data?.[0] ?? {};
      if (item.schema !== undefined) setSchema(item.schema as object);
      if (item.uiSchema !== undefined) setUiSchema(item.uiSchema as object);
    },
  });

  function emitSchemaLoaded(s: object, ui: object) {
    emit({ type: 'SchemaLoaded', data: [{ schema: s, uiSchema: ui }] });
  }

  function handleNew() {
    setLoadedDataClass({});
    setTitleValue('');
    setSchema({});
    setUiSchema({});
    setContainer('');
    setError(undefined);
    setSchemaType(schemaTypeOptions[0]);
    emitSchemaLoaded({}, {});
  }

  function handleLoadByUuid(uuid: string) {
    dataClassLoad.mutateAsync(uuid).then((response) => {
      const dc = response.dataClass;
      setLoadedDataClass(dc);
      setSchema(dc.schema);
      setUiSchema(dc.uiSchema);
      setTitleValue(dc.title ?? '');
      setContainer(dc.container);
      const matchedType = schemaTypeOptions.find((o) => o.value === dc.schemaType);
      if (matchedType) setSchemaType(matchedType);
      emitSchemaLoaded(dc.schema, dc.uiSchema);
    });
  }

  function handleSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
  }

  const handleCreateNew = (e: SyntheticEvent) => {
    e.preventDefault();
    if (!formRef.current) return;
    setCreateNewDataClassLoading(true);
    const formData = new FormData(formRef.current);
    const queryText = (formData.get('queryText') ?? '') as string;
    dataClassCreate
      .mutateAsync({ input: queryText, schema, uiSchema, container })
      .then((created) => {
        setSchema(created.dataClass.schema);
        setUiSchema(created.dataClass.uiSchema);
        emitSchemaLoaded(created.dataClass.schema, created.dataClass.uiSchema);
        setCreateNewDataClassLoading(false);
      });
  };

  const handleRefineExisting = (e: SyntheticEvent) => {
    e.preventDefault();
    if (!formRef.current) return;
    setRefineDataClassLoading(true);
    const formData = new FormData(formRef.current);
    const queryText = (formData.get('queryText') ?? '') as string;
    dataClassRefine
      .mutateAsync({ input: queryText, schema, uiSchema, container })
      .then((refined) => {
        setSchema(refined.dataClass.schema);
        setUiSchema(refined.dataClass.uiSchema);
        emitSchemaLoaded(refined.dataClass.schema, refined.dataClass.uiSchema);
        setRefineDataClassLoading(false);
      });
  };

  const handleDataClassSaveConfirmation = (e: SyntheticEvent) => {
    e.preventDefault();
    setError(undefined);

    if (!titleValue.trim()) {
      setError('Title is required.');
      return;
    }
    if (loadedDataClass?.uuid) {
      setShowSaveModal(true);
    } else {
      handleDataClassSave();
    }
  };

  const handleDataClassSave = () => {
    const objectData: ObjectData = {
      uuid: loadedDataClass?.uuid ?? undefined,
      schema,
      uiSchema,
      title: titleValue,
      container,
      schemaType: schemaType.value,
    };
    objectSchemaUpsertMutation.mutate(objectData, {
      onSuccess: (result) => {
        setShowSaveModal(false);
        if (!loadedDataClass?.uuid) {
          setLoadedDataClass({ ...loadedDataClass, uuid: result.uuid, title: titleValue });
        }
      },
    });
  };

  const handleDataClassDelete = () => {
    if (!loadedDataClass?.uuid) return;
    objectSchemaDeleteMutation.mutate(loadedDataClass.uuid, {
      onSuccess: () => {
        setShowDeleteModal(false);
        handleNew();
      },
    });
  };

  function handleDrop(data: ContainerDropData) {
    const uuid = data?.value?.[0]?.uuid;
    if (uuid) setContainer(uuid);
  }

  const newButton = createPortal(
    <Button
      variant="tertiary"
      onClick={handleNew}
      className="py-1.5 bg-indigo-700 text-white hover:bg-indigo-500 focus:ring-indigo-500 focus:ring-offset-2 inline-flex items-center rounded-md border border-transparent px-2 text-sm font-medium focus:outline-none focus:ring-2"
    >
      New
    </Button>,
    document.getElementById(`widget-actions-${id}`) || document.body
  );

  return (
    <Widget id={id} title="Schema Details">
      <form
        ref={formRef}
        className="grid gap-y-4 items-start content-start"
        onSubmit={handleSearch}
      >
        <p className="text-sm bg-gray-100 dark:bg-gray-700 rounded-md p-2 text-gray-700 dark:text-gray-400 text-center">
          {loadedDataClass?.uuid
            ? `You are editing "${loadedDataClass.title || loadedDataClass.uuid}"`
            : 'You are creating a new schema.'}
        </p>

        <div>
          <label htmlFor="title" className="block text-sm font-medium text-gray-900 dark:text-gray-100">
            Title
          </label>
          <div className="mt-1">
            <input
              name="title"
              id="title"
              type="text"
              value={titleValue}
              onChange={(e) => {
                setTitleValue(e.target.value);
                if (error === 'Title is required.') setError(undefined);
              }}
              className={clsx(
                'block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6 bg-white dark:bg-gray-800',
                error === 'Title is required.'
                  ? 'ring-red-500 focus:ring-red-600'
                  : 'ring-gray-300 dark:ring-gray-600 focus:ring-indigo-600 dark:focus:ring-indigo-400'
              )}
            />
            {error ? <p className="mt-1 text-sm text-red-500 dark:text-red-400">{error}</p> : null}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-900 dark:text-gray-100">Type</label>
          <div className="mt-1">
            <SelectMenu
              label="Type"
              hideLabel
              options={schemaTypeOptions}
              selected={schemaType}
              onChange={(value) => value && setSchemaType(value)}
            />
          </div>
        </div>

        <div>
          <label htmlFor="queryText" className="block text-sm font-medium text-gray-900 dark:text-gray-100">
            Paste Data
          </label>
          <div className="mt-1">
            <textarea
              name="queryText"
              id="queryText"
              rows={5}
              placeholder="Paste your data here"
              className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-indigo-600 dark:focus:ring-indigo-400 sm:text-sm sm:leading-6 bg-white dark:bg-gray-800"
            />
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <Button type="submit" loading={createNewDataClassLoading} onClick={handleCreateNew}>
            Create New
          </Button>
          <Button type="submit" loading={refineDataClassLoading} onClick={handleRefineExisting}>
            Refine Existing
          </Button>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-900 dark:text-gray-100">Container</label>
          <div className="space-y-1 mt-1">
            <DragDrop
              allowedTypes={['object/insert']}
              onDrop={(data) => handleDrop(data as unknown as ContainerDropData)}
              className="relative flex items-center space-x-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-6 py-5 shadow-sm focus-within:ring-2 focus-within:ring-indigo-600 dark:focus-within:ring-indigo-400 focus-within:ring-offset-2 hover:border-gray-400 dark:hover:border-gray-500 border-dashed justify-center text-center h-5"
              droppingClassName="relative flex items-center space-x-3 rounded-lg border border-gray-300 dark:border-gray-600 px-6 py-5 shadow-sm focus-within:ring-2 focus-within:ring-indigo-600 dark:focus-within:ring-indigo-400 focus-within:ring-offset-2 hover:border-gray-800 dark:hover:border-gray-400 border-dashed bg-gray-50 dark:bg-gray-700 flex justify-center items-center h-5"
            >
              {() => <PlusIcon className="h-4 w-4 dark:text-gray-400" />}
            </DragDrop>
            {container ? (
              <div className="relative flex items-center gap-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 pl-2 pr-3 shadow-sm text-xs">
                <div className="relative w-10 h-10 bg-purple-50 dark:bg-purple-900/20 rounded-full flex justify-center items-center text-center">
                  <LinkIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-gray-900 dark:text-gray-100">{container}</p>
                </div>
                <button
                  onClick={() => setContainer('')}
                  className="text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                  type="button"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            ) : null}
            <input type="hidden" name="container" value={container} />
          </div>
        </div>

        {newButton}

        <div className="flex gap-x-2 pt-2">
          <Button
            variant="tertiary"
            loading={objectSchemaUpsertMutation.isLoading}
            type="submit"
            onClick={handleDataClassSaveConfirmation}
          >
            Save
          </Button>
          {loadedDataClass?.uuid && (
            <Button
              variant="tertiary"
              type="button"
              loading={objectSchemaDeleteMutation.isLoading}
              onClick={() => setShowDeleteModal(true)}
              className="inline-flex items-center rounded-md border border-transparent bg-red-600 px-2 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-red-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
            >
              Delete
            </Button>
          )}
        </div>

        {showDeleteModal && (
          <Modal
            onCancel={() => setShowDeleteModal(false)}
            onSubmit={handleDataClassDelete}
            loading={objectSchemaDeleteMutation.isLoading}
            title="Delete Schema"
            message="Are you sure you want to delete this schema? This action cannot be undone."
            actionName="Delete"
            offsetSidebar
          />
        )}

        {showSaveModal && (
          <Modal
            onCancel={() => { setShowSaveModal(false); setError(undefined); }}
            onSubmit={handleDataClassSave}
            loading={objectSchemaUpsertMutation.isLoading}
            title={modalText.overwriteDataClassTitle}
            message={modalText.overwriteDataClassMessage}
            actionName={modalText.overwriteDataClassActionName}
            offsetSidebar
          />
        )}
      </form>
    </Widget>
  );
}
